import React, { useState, useContext, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";

import MenuItem from "@material-ui/core/MenuItem";
import { Menu } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import apiV2 from "../../services/apiV2";
import ConfirmationModal from "../ConfirmationModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import EmailMessageModal from "../EmailMessageModal";
import PermissionModal from "../PermissionModal";
import AppModal from "../AppModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { resolveMediaUrl } from "../../helpers/resolveMediaUrl";

function resolveMessageMediaUrl(mediaUrl) {
	return resolveMediaUrl(mediaUrl);
}

function guessAudioFilename(url, fallbackBody) {
	try {
		const pathname = new URL(url, window.location.origin).pathname;
		const base = pathname.split("/").filter(Boolean).pop();
		if (base) return decodeURIComponent(base.split("?")[0]);
	} catch (_) {
		// ignore
	}
	if (fallbackBody && /\.\w{2,5}$/.test(fallbackBody)) return fallbackBody;
	return "audio.ogg";
}

async function downloadAudioFromMessage(message) {
	const url = resolveMessageMediaUrl(message?.mediaUrl);
	if (!url) {
		toast.error("Não foi possível baixar o áudio");
		return;
	}

	const filename = guessAudioFilename(url, message?.body);

	try {
		const response = await fetch(url, { credentials: "include" });
		if (!response.ok) throw new Error("download failed");
		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = objectUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(objectUrl);
	} catch (_) {
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		link.remove();
	}
}

function MessageOptionsMenu({
	message,
	menuOpen,
	handleClose,
	anchorEl,
	selectedMessages,
	setSelectedMessages,
	translateMensagem,
	tailwickLayout = false,
	ticketChannel,
}) {
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [emailOpen, setEmailOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [editBody, setEditBody] = useState("");
	const [editing, setEditing] = useState(false);
	const [menuStyle, setMenuStyle] = useState(null);
	const [savingSticker, setSavingSticker] = useState(false);
	const [downloadingAudio, setDownloadingAudio] = useState(false);
	const { user } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);

	const handleDeleteMessage = async () => {
		try {
			await apiV2.delete(`/message/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const canEditMessage = () => {
		if (!message?.fromMe || message?.isDeleted) return false;
		if (message?.mediaType && message.mediaType !== "chat") return false;
		const channel = String(ticketChannel || "").toLowerCase();
		if (channel && channel !== "whatsapp") return false;
		const created = message?.createdAt ? new Date(message.createdAt).getTime() : 0;
		if (!created) return false;
		return Date.now() - created <= 20 * 60 * 1000;
	};

	const handleOpenEdit = () => {
		setEditBody(message?.body || "");
		setEditOpen(true);
		handleClose();
	};

	const handleConfirmEdit = async () => {
		const body = String(editBody || "").trim();
		if (!body) {
			toast.error(i18n.t("messageOptionsMenu.editEmpty"));
			return;
		}
		if (editing) return;
		setEditing(true);
		try {
			await apiV2.put(`/message/${message.id}`, { body });
			toast.success(i18n.t("messageOptionsMenu.editSuccess"));
			setEditOpen(false);
		} catch (err) {
			toastError(err);
		} finally {
			setEditing(false);
		}
	};

	const hanldeReplyMessage = () => {
		if (message.mediaType !== "email") {
			setReplyingMessage(message);
			handleClose();
		} else {
			user.profile === "admin" || user.canDeleteMessages
				? setConfirmationOpen(true)
				: setPermissionOpen(true);
		}
	};

	const handleOpenConfirmationModal = () => {
		user.profile === "admin" || user.canDeleteMessages
			? setConfirmationOpen(true)
			: setPermissionOpen(true);
		handleClose();
	};

	const handleReplyEmail = () => {
		setEmailOpen(true);
		handleClose();
	};

	const handleSelectedMessages = () => {
		if (!selectedMessages.includes(message.id)) {
			setSelectedMessages([...selectedMessages, message.id]);
		}
	};

	const handleSaveSticker = async (isCompanyShared = false) => {
		if (savingSticker) return;
		setSavingSticker(true);
		try {
			const { data } = await apiV2.post("/stickers", {
				messageId: message.id,
				isCompanyShared: Boolean(isCompanyShared),
			});
			if (data?.created === false) {
				toast.info(i18n.t("messageOptionsMenu.stickerAlreadySaved"));
			} else {
				toast.success(i18n.t("messageOptionsMenu.stickerSaved"));
			}
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSavingSticker(false);
		}
	};

	const handleDownloadAudio = async () => {
		if (downloadingAudio) return;
		setDownloadingAudio(true);
		try {
			await downloadAudioFromMessage(message);
			handleClose();
		} finally {
			setDownloadingAudio(false);
		}
	};

	const menuItems = useMemo(() => {
		if (!message?.id) return [];

		const isSticker = message.mediaType === "sticker";
		const isAudio = message.mediaType === "audio";
		const items = [];

		if (message.mediaType !== "email") {
			items.push({
				key: "reply",
				label: i18n.t("messageOptionsMenu.reply"),
				onClick: hanldeReplyMessage,
			});
		}

		if (message.mediaType === "email") {
			items.push({
				key: "reply-email",
				label: i18n.t("messageOptionsMenu.reply"),
				onClick: handleReplyEmail,
			});
		}

		if (isSticker) {
			items.push({
				key: "save-sticker",
				label: i18n.t("messageOptionsMenu.saveSticker"),
				onClick: () => handleSaveSticker(false),
			});
			const profile = String(user?.profile || "").toLowerCase();
			if (profile === "admin" || profile === "master") {
				items.push({
					key: "save-sticker-company",
					label: i18n.t("messageOptionsMenu.saveStickerCompany"),
					onClick: () => handleSaveSticker(true),
				});
			}
		}

		if (
			message.mediaType === "chat" ||
			message.mediaType === "image" ||
			message.mediaType === "audio" ||
			message.mediaType === "video"
		) {
			items.push({
				key: "forward",
				label: "Encaminhar",
				onClick: () => {
					handleSelectedMessages();
					handleClose();
				},
			});
		}

		if (isAudio) {
			items.push({
				key: "download-audio",
				label: i18n.t("messageOptionsMenu.downloadAudio"),
				onClick: handleDownloadAudio,
			});
		}

		if (!isSticker && !isAudio) {
			items.push({
				key: "translate",
				label: "Traduzir",
				onClick: () => {
					translateMensagem(message);
					handleClose();
				},
			});
		}

		if (canEditMessage()) {
			items.push({
				key: "edit",
				label: i18n.t("messageOptionsMenu.edit"),
				onClick: handleOpenEdit,
			});
		}

		if (
			message.fromMe &&
			message.mediaType !== "email" &&
			message.mediaType !== "template"
		) {
			items.push({
				key: "delete",
				label: i18n.t("messageOptionsMenu.delete"),
				onClick: handleOpenConfirmationModal,
				danger: true,
			});
		}

		return items;
	}, [message, selectedMessages, translateMensagem, handleClose, user, savingSticker, downloadingAudio, ticketChannel]);

	useEffect(() => {
		if (!tailwickLayout || !menuOpen || !anchorEl) {
			setMenuStyle(null);
			return;
		}

		const updatePosition = () => {
			const rect = anchorEl.getBoundingClientRect();
			setMenuStyle({
				top: rect.bottom + 6,
				left: rect.left + 100 < window.innerWidth ? rect.left : window.innerWidth - 200,
			});
		};

		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [tailwickLayout, menuOpen, anchorEl]);

	useEffect(() => {
		if (!tailwickLayout || !menuOpen) return;

		const handlePointerDown = (event) => {
			const menuNode = document.getElementById("tailwick-message-options-menu");
			if (menuNode?.contains(event.target)) return;
			if (anchorEl?.contains(event.target)) return;
			handleClose();
		};

		document.addEventListener("mousedown", handlePointerDown);
		return () => document.removeEventListener("mousedown", handlePointerDown);
	}, [tailwickLayout, menuOpen, anchorEl, handleClose]);

	const modals = (
		<>
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteMessage}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<AppModal
				open={editOpen}
				onClose={() => !editing && setEditOpen(false)}
				title={i18n.t("messageOptionsMenu.editModal.title")}
				maxWidth={480}
				footer={
					<>
						<button
							type="button"
							className="btn btn-sub-gray"
							disabled={editing}
							onClick={() => setEditOpen(false)}
						>
							{i18n.t("confirmationModal.buttons.cancel")}
						</button>
						<button
							type="button"
							className="btn btn-primary"
							disabled={editing}
							onClick={handleConfirmEdit}
						>
							{i18n.t("messageOptionsMenu.editModal.confirm")}
						</button>
					</>
				}
			>
				<textarea
					value={editBody}
					onChange={(e) => setEditBody(e.target.value)}
					rows={4}
					style={{
						width: "100%",
						marginTop: 8,
						padding: 8,
						borderRadius: 8,
						border: "1px solid #ccc",
						resize: "vertical",
						fontFamily: "inherit",
						fontSize: 14,
						boxSizing: "border-box",
					}}
					disabled={editing}
				/>
			</AppModal>
			<EmailMessageModal
				modalOpened={emailOpen}
				handleModalOpened={() => setEmailOpen(false)}
				ticketId={message?.ticketId}
				message={message}
			/>
		</>
	);

	if (tailwickLayout) {
		const tailwickMenu =
			menuOpen && menuStyle && menuItems.length > 0
				? ReactDOM.createPortal(
						<div
							id="tailwick-message-options-menu"
							className="tailwick-message-options-menu"
							data-theme={darkMode ? "dark" : "light"}
							role="menu"
							style={{
								position: "fixed",
								top: menuStyle.top,
								left: menuStyle.left,
								zIndex: 1400,
							}}
						>
							{menuItems.map((item) => (
								<button
									key={item.key}
									type="button"
									role="menuitem"
									className={`tailwick-message-options-menu__item${
										item.danger ? " tailwick-message-options-menu__item--danger" : ""
									}`}
									onClick={item.onClick}
								>
									{item.label}
								</button>
							))}
						</div>,
						document.body
				  )
				: null;

		return (
			<>
				{modals}
				{tailwickMenu}
			</>
		);
	}

	return (
		<>
			{modals}
			<Menu
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				open={menuOpen}
				onClose={handleClose}
				PaperProps={{
					sx: {
						borderRadius: 2,
					},
				}}
			>
				{menuItems.map((item) => (
					<MenuItem key={item.key} onClick={item.onClick} style={{ padding: "1rem" }}>
						{item.label}
					</MenuItem>
				))}
			</Menu>
		</>
	);
}

export default MessageOptionsMenu;
